import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../../../backend/convex/_generated/api';
import { Id } from '../../../../backend/convex/_generated/dataModel';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { Icons } from '@/components/admin/Icons';
import { Link, useNavigate } from 'react-router-dom';
import { createBookUrl } from '@/utils/slug';
import { useAuth } from '@/context/use-auth';
import type { PublisherBook } from '@/types';
import type { BookAnalytics } from '@/types';

export function AdminBooks() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const books = useQuery(api.publisher.listAllBooks) as PublisherBook[] | undefined;
  const bookAnalytics = useQuery(
    api.admin.getBookAnalytics,
    user ? { clerkId: user.id } : "skip"
  ) as BookAnalytics[] | undefined;
  const deleteBook = useMutation(api.publisher.deleteBook);
  const togglePublished = useMutation(api.admin.toggleBookPublished);
  const duplicateBook = useMutation(api.admin.duplicateBook);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState<'all' | 'published' | 'drafts'>('all');
  const [isDeleting, setIsDeleting] = useState<string | null>(null);

  const filteredBooks = books?.filter((book) => {
    const matchesSearch = 
      book.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      book.author.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = 
      filter === 'all' ? true :
      filter === 'published' ? book.isPublished :
      !book.isPublished;
    
    return matchesSearch && matchesFilter;
  });

  const handleDelete = async (bookId: string) => {
    if (!confirm('Are you sure you want to delete this book? This action cannot be undone.')) {
      return;
    }
    
    setIsDeleting(bookId);
    try {
      await deleteBook({ bookId: bookId as Id<'books'> });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Error deleting book: ' + message);
    } finally {
      setIsDeleting(null);
    }
  };

  const handleTogglePublished = async (bookId: string) => {
    if (!user) return;
    try {
      await togglePublished({ bookId: bookId as Id<'books'>, clerkId: user.id });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Error: ' + message);
    }
  };

  const handleDuplicate = async (bookId: string) => {
    if (!user) return;
    try {
      const result = await duplicateBook({ bookId: bookId as Id<'books'>, clerkId: user.id });
      if (result.newBookId) {
        navigate(`/admin/editor?edit=${result.newBookId}`);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      alert('Error duplicating book: ' + message);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <header className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-stone-800 mb-2">Books</h1>
            <p className="text-stone-500">Manage your library</p>
          </div>
          <button
            onClick={() => navigate('/admin/editor?new=true')}
            className="px-4 py-2 bg-stone-800 text-white text-sm font-medium rounded-lg hover:bg-stone-900 transition-colors flex items-center gap-2"
          >
            <Icons.Plus />
            New Book
          </button>
        </header>

        {/* Filters */}
        <div className="mb-6 flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1 max-w-md">
            <input
              type="text"
              placeholder="Search books..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 pl-10 bg-white border border-stone-200 rounded-lg text-stone-800 placeholder-stone-300 focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400"
            />
            <div className="absolute left-3 top-2.5 text-stone-400">
              <Icons.Search />
            </div>
          </div>

          <div className="flex gap-2">
            {(['all', 'published', 'drafts'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  filter === f
                    ? 'bg-stone-800 text-white'
                    : 'bg-white border border-stone-200 text-stone-600 hover:border-stone-300'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Books List */}
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          {!books ? (
            <div className="p-6 space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-20 bg-stone-100 rounded animate-pulse" />
              ))}
            </div>
          ) : filteredBooks?.length === 0 ? (
            <div className="text-center py-16 text-stone-400">
              <div className="w-12 h-12 mx-auto mb-4">
                <Icons.Books />
              </div>
              <p className="text-lg">No books found</p>
              <button
                onClick={() => navigate('/admin/editor?new=true')}
                className="mt-4 text-teal-600 hover:text-teal-700 font-medium inline-block"
              >
                Create your first book →
              </button>
            </div>
          ) : (
            <div className="divide-y divide-stone-100">
              {filteredBooks?.map((book) => (
                <div
                  key={book._id}
                  className="flex items-center gap-4 p-4 hover:bg-stone-50 transition-colors"
                >
                  {book.coverUrl ? (
                    <img 
                      src={book.coverUrl} 
                      alt="" 
                      className="w-12 h-16 object-cover rounded bg-stone-100" 
                    />
                  ) : (
                    <div className="w-12 h-16 bg-stone-100 rounded flex items-center justify-center text-stone-300">
                      <span className="text-xl">§</span>
                    </div>
                  )}
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-medium text-stone-800 truncate">{book.title}</h3>
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        book.isPublished 
                          ? 'bg-teal-100 text-teal-700' 
                          : 'bg-stone-100 text-stone-600'
                      }`}>
                        {book.isPublished ? 'Published' : 'Draft'}
                      </span>
                    </div>
                    <p className="text-sm text-stone-500">{book.author}</p>
                    <div className="flex items-center gap-3 mt-1">
                      <p className="text-xs text-stone-400">
                        {book.chapterCount} chapters • {book.audioCount} audio
                      </p>
                      {bookAnalytics && (() => {
                        const analytics = bookAnalytics.find((a) => a.bookId === book._id);
                        if (analytics && analytics.unlockCount > 0) {
                          return (
                            <p className="text-xs text-teal-600 font-medium">
                              {analytics.unlockCount} unlocks • {analytics.totalSpent} coins
                            </p>
                          );
                        }
                        return null;
                      })()}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1">
                    <Link
                      to={`/admin/editor?edit=${book._id}`}
                      className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                      title="Edit"
                    >
                      <Icons.Edit />
                    </Link>
                    
                    <Link
                      to={createBookUrl(book.title, book._id)}
                      className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                      title="View"
                    >
                      <Icons.Eye />
                    </Link>

                    <button
                      onClick={() => handleDuplicate(book._id)}
                      className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                      title="Duplicate"
                    >
                      <Icons.Duplicate />
                    </button>

                    <button
                      onClick={() => handleTogglePublished(book._id)}
                      className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded transition-colors"
                      title={book.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      {book.isPublished ? <Icons.Lock /> : <Icons.Unlock />}
                    </button>

                    <button
                      onClick={() => handleDelete(book._id)}
                      disabled={isDeleting === book._id}
                      className="p-2 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors disabled:opacity-50"
                      title="Delete"
                    >
                      {isDeleting === book._id ? <Icons.Loading /> : <Icons.Trash />}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
